import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Package, Plus, Loader2, Calendar, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config";

const PalletLogDashboard = () => {
  const { user } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedWO, setSelectedWO] = useState("");
  
  // Form State
  const [formData, setFormData] = useState({
    case_quantity: "",
    lot_number: "",
  });

  const todayStr = format(new Date(), "yyyy-MM-dd");

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [woRes, palletRes] = await Promise.all([
        axios.get(`${API}/production/today`),
        axios.get(`${API}/pallets?date=${todayStr}`)
      ]);
      setWorkOrders(woRes.data);
      setPallets(palletRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    let targetId = selectedWO;

    // 1. If no Work Order is currently selected, pick the first one available
    if (!targetId && workOrders.length > 0) {
      targetId = workOrders[0].wo_id;
    }

    // 2. Force the form update logic (Lot Code calculation) to run for the target
    if (targetId) {
      handleWOChange(targetId);
    }

    setShowModal(true);
  };

  const handleWOChange = (woId) => {
    setSelectedWO(woId);
    
    // Find pallets for this WO
    const wo = workOrders.find(w => w.wo_id === woId);
    const relatedPallets = pallets.filter(p => p.work_order_id === woId);
    
    // 1. Calculate Default Quantity
    let defaultQty = "";
    if (relatedPallets.length > 0) {
      defaultQty = relatedPallets[0].case_quantity;
    }

    // 2. Auto-Calculate Lot Code (Line-YYJJJ)
    let autoLotCode = "";
    if (wo && wo.planned_date) {
      try {
        const date = new Date(wo.planned_date);
        const start = new Date(date.getFullYear(), 0, 0);
        const diff = date - start;
        const oneDay = 1000 * 60 * 60 * 24;
        const dayOfYear = Math.floor(diff / oneDay);
        
        const yy = date.getFullYear().toString().slice(-2);
        const jjj = dayOfYear.toString().padStart(3, '0');
        const line = wo.production_line || "L1";
        
        autoLotCode = `${line}-${yy}${jjj}`;
      } catch (e) {
        console.error("Error generating lot code", e);
      }
    }

    setFormData(prev => ({
      ...prev,
      case_quantity: defaultQty,
      // Auto-fill lot number based on logic
      lot_number: autoLotCode 
    }));
  };

  const handleSubmit = async () => {
    if (!selectedWO || !formData.case_quantity) {
      toast.error("Please select a Work Order and enter Quantity");
      return;
    }

    try {
      const payload = {
        case_quantity: parseFloat(formData.case_quantity),
        lot_number: formData.lot_number || "N/A",
        created_by: user?.display_name || user?.email || "Pack Team"
      };

      await axios.post(`${API}/production/${selectedWO}/pallets`, payload);
      
      toast.success("Pallet logged successfully");
      setShowModal(false);
      fetchData(); // Refresh immediately
    } catch (error) {
      console.error(error);
      toast.error("Failed to log pallet");
    }
  };

  // Calculate Progress for a WO
  const getProgress = (wo) => {
    const planned = wo.planned_quantity || 1;
    const actual = wo.actual_quantity || 0;
    return Math.min(100, Math.round((actual / planned) * 100));
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pallet Log Dashboard</h1>
          <p className="text-gray-500">{format(new Date(), "EEEE, MMMM do, yyyy")}</p>
        </div>
        <Button onClick={handleOpenModal} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Pallet
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT PANE: Today's Work Orders */}
        <div className="lg:col-span-1 space-y-4">
          <h2 className="text-lg font-semibold text-gray-700">Today's Work Orders</h2>
          {workOrders.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                No active work orders for today.
              </CardContent>
            </Card>
          ) : (
            workOrders.map(wo => {
              const progress = getProgress(wo);
              return (
                <Card key={wo.wo_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{wo.production_line}</h3>
                        <p className="text-sm text-gray-500 font-mono">{wo.wo_number}</p>
                      </div>
                      <Badge variant={wo.status === 'in_production' ? 'default' : 'secondary'}>
                        {wo.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-800 line-clamp-2" title={wo.item_name}>
                        {wo.item_name}
                      </p>
                    </div>

                    {/* Circular Progress Visual (Simplified as Bar for reliability) */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Progress</span>
                        <span>{wo.actual_quantity} / {wo.planned_quantity} cs</span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-600 transition-all duration-500" 
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="text-right text-xs font-bold text-blue-600 mt-1">{progress}%</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* RIGHT PANE: Pallet Log */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader className="border-b bg-white">
              <CardTitle>Pallet Log</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>LP Number</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Lot Code</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pallets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No pallets logged today.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pallets.map((pallet) => (
                      <TableRow key={pallet.pallet_id}>
                        <TableCell className="text-xs text-gray-500">
                          {format(new Date(pallet.created_at), "hh:mm a")}
                        </TableCell>
                        <TableCell className="font-mono font-medium text-xs">
                          {pallet.lp_number}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm" title={pallet.item_name}>
                          {pallet.item_name}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {pallet.lot_code || pallet.lot_number || "-"}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {pallet.case_quantity}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex w-fit items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Completed
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Pallet Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Pallet</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Work Order / Item</Label>
              <Select value={selectedWO} onValueChange={handleWOChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Work Order" />
                </SelectTrigger>
                <SelectContent>
                  {workOrders.map(wo => (
                    <SelectItem key={wo.wo_id} value={wo.wo_id}>
                      {wo.wo_number} - {wo.item_name.substring(0, 30)}...
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label>Quantity (Cases)</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  value={formData.case_quantity} 
                  onChange={(e) => setFormData({...formData, case_quantity: e.target.value})}
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500">
                {formData.case_quantity ? "Pre-filled from last pallet" : "Enter cases per pallet"}
              </p>
            </div>

            <div className="grid gap-2">
              <Label>Lot Number (Optional)</Label>
              <Input 
                value={formData.lot_number} 
                onChange={(e) => setFormData({...formData, lot_number: e.target.value})}
                placeholder="Auto-generated if empty"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Save Pallet</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PalletLogDashboard;